import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { MessageImage } from 'types/interfaces/message';
import './ChatImageLightbox.css';

interface Props {
  images: MessageImage[];
  initialIndex: number;
  onClose: () => void;
}

export const ChatImageLightbox: React.FC<Props> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const hasMultiple = images.length > 1;

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (hasMultiple && e.key === 'ArrowRight') {
        goNext();
      } else if (hasMultiple && e.key === 'ArrowLeft') {
        goPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, hasMultiple, goNext, goPrev]);

  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  const lightboxContent = (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose} type="button">
          &times;
        </button>

        {hasMultiple && (
          <button className="lightbox-nav lightbox-nav-prev" onClick={goPrev} type="button">
            &#8249;
          </button>
        )}

        <img
          src={currentImage.url}
          alt={`Attachment ${currentIndex + 1} of ${images.length}`}
          className="lightbox-image"
        />

        {hasMultiple && (
          <button className="lightbox-nav lightbox-nav-next" onClick={goNext} type="button">
            &#8250;
          </button>
        )}

        {hasMultiple && (
          <div className="lightbox-counter">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(lightboxContent, document.body);
};
